import Aegis from "aegis-web-sdk";

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// 当前环境
// const isDev = process.env.NODE_ENV === "development";
const isDev = true;

class Logger {
  constructor() {
    this.level = isDev ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO;

    // 初始化 Aegis
    if (!isDev) {
      this.aegis = new Aegis({
        id: process.env.VUE_APP_TENCENT_LOG_KEY || "x", // 上报 id
        reportApiSpeed: true, // 接口测速
        reportAssetSpeed: false, // 静态资源测速
        spa: false, // spa 应用页面跳转的时候开启 pv 计算
        api: {
          apiDetail: true,
          reportRequest: true,
          usePerformanceTiming: true,
        },
        hostUrl: "https://rumt-zh.com",
      });
    }
  }

  setLevel(level) {
    this.level = level;
  }

  // 格式化日志内容
  formatLogContent(content) {
    if (content === null) return "null";
    if (content === undefined) return "undefined";

    if (content instanceof Error) {
      return content.message;
    }

    if (typeof content === "object") {
      try {
        return JSON.stringify(content);
      } catch (e) {
        return "[Object]";
      }
    }

    return String(content);
  }

  _log(level, type, ...args) {
    if (level >= this.level) {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}][${type}]`;

      // 开发环境显示完整日志
      if (isDev) {
        const formattedArgs = args.map((arg) => {
          if (arg instanceof Error) {
            console[type](arg.stack); // 错误对象单独打印堆栈
            return arg.message;
          }
          return arg;
        });
        console[type](prefix, ...formattedArgs);
      } else {
        // 生产环境上报到日志服务
        this.report(type, args);
      }
    }
  }

  debug(...args) {
    this._log(LOG_LEVELS.DEBUG, "debug", ...args);
  }

  info(...args) {
    this._log(LOG_LEVELS.INFO, "info", ...args);
  }

  warn(...args) {
    this._log(LOG_LEVELS.WARN, "warn", ...args);
  }

  error(...args) {
    this._log(LOG_LEVELS.ERROR, "error", ...args);
  }

  // 特殊场景的日志
  api(action, ...args) {
    this._log(LOG_LEVELS.INFO, "info", `[API][${action}]`, ...args);
  }

  wechat(...args) {
    this._log(LOG_LEVELS.INFO, "info", "[WeChat]", ...args);
  }

  // 上报到日志服务
  report(type, args) {
    if (!isDev && this.aegis) {
      try {
        const formattedArgs = args.map((arg) => this.formatLogContent(arg));

        const logData = {
          msg: Array.isArray(formattedArgs)
            ? formattedArgs.join(" ")
            : formattedArgs,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        };

        // 根据日志类型选择上报方法
        switch (type) {
          case "error":
            if (args[0] instanceof Error) {
              this.aegis.error(args[0]); // 直接上报错误对象
            } else {
              this.aegis.error({
                ...logData,
                level: Aegis.logType.ERROR,
              });
            }
            break;

          case "info":
            this.aegis.info({
              ...logData,
              level: Aegis.logType.INFO,
            });
            break;

          case "warn":
            this.aegis.report({
              ...logData,
              level: Aegis.logType.REPORT,
              type: "warning",
            });
            break;

          case "debug":
            if (isDev) {
              this.aegis.info({
                ...logData,
                level: Aegis.logType.INFO,
                type: "debug",
              });
            }
            break;

          // 特殊场景日志
          case "api":
            this.aegis.report({
              ...logData,
              level: Aegis.logType.API_RESPONSE,
            });
            break;

          case "wechat":
            this.aegis.report({
              ...logData,
              level: Aegis.logType.REPORT,
              type: "wechat",
            });
            break;
        }
      } catch (e) {
        console.error("日志上报失败:", e);
      }
    }
  }

  // 添加静态日志方法
  static log(type, ...args) {
    // 使用已创建的 logger 实例
    if (logger) {
      logger[type](...args);
    } else {
      console[type](...args);
    }
  }
}

// 创建一个全局的 logger 实例
const logger = new Logger();

// 添加到全局
window.$log = logger;

// 添加 Vue 插件
export const LoggerPlugin = {
  install(Vue) {
    Vue.prototype.$log = logger;
  },
};

export { logger, Logger };
export default logger;
